import { useEffect, useState } from 'react';
import type { Differential, RivalComparison, Squad } from '@fantasy/types';
import { ApiRequestError, api } from '../api.ts';

interface RivalDifferentialsProps {
  entryId: number;
  squad: Squad | null;
}

/**
 * `Tracker`'s per-manager expansion (T-062): what this rival owns that the
 * loaded squad does not, and vice versa, each ranked by `scorePlayer` so the
 * most consequential difference reads first. Shared players are a bare count
 * — a comparison is about the differences, and a list of a dozen-plus shared
 * names would bury them. Fetches on mount and whenever the loaded squad
 * changes, since the server compares against whichever squad it has on file.
 */
export function RivalDifferentials({ entryId, squad }: RivalDifferentialsProps) {
  const [comparison, setComparison] = useState<RivalComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!squad) {
      setComparison(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getDifferentials(entryId)
      .then(({ comparison: fetched }) => {
        if (!cancelled) setComparison(fetched);
      })
      .catch((err) => {
        if (!cancelled) {
          setComparison(null);
          setError(err instanceof ApiRequestError ? err.message : 'Could not load this comparison.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, squad?.id]);

  if (!squad) {
    return <p className="hint">Load a squad first to compare it against this manager.</p>;
  }

  if (loading) {
    return (
      <p className="hint" aria-live="polite">
        Loading comparison…
      </p>
    );
  }

  if (error) {
    return <p className="hint text-bad">{error}</p>;
  }

  if (!comparison) return null;

  const { pointsGap, rivalOnly, ownOnly, shared } = comparison;

  return (
    <div className="rival-differentials">
      <p className="hint">{pointsGapText(pointsGap)}</p>

      <div className="rival-differentials-columns">
        <DifferentialColumn title="They have, you don't" players={rivalOnly} />
        <DifferentialColumn title="You have, they don't" players={ownOnly} />
      </div>

      <p className="hint mt-md">
        {shared.length} player{shared.length === 1 ? '' : 's'} shared by both squads.
      </p>
    </div>
  );
}

function pointsGapText(pointsGap: RivalComparison['pointsGap']): string {
  if (pointsGap.direction === 'level') return 'Level on season points.';
  const verb = pointsGap.direction === 'ahead' ? 'ahead of' : 'behind';
  return `${pointsGap.points} point${pointsGap.points === 1 ? '' : 's'} ${verb} this manager.`;
}

function DifferentialColumn({ title, players }: { title: string; players: Differential[] }) {
  return (
    <div className="rival-differentials-column">
      <h5>{title}</h5>
      {players.length === 0 ? (
        <p className="hint">None.</p>
      ) : (
        <ul className="list-reset">
          {players.map((player) => (
            <li key={player.fplId ?? player.name} className="rival-differentials-player">
              <span>{player.name}</span>
              <span className="hint">
                {player.position} · {player.club}
              </span>
              <span className="num">{player.score}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
